import { Alert, Button, CircularProgress, Link, Stack, Typography } from '@mui/material';

import { OtpField, TextField } from '@/components/forms';
import { AuthCard } from '@/features/identity/components/AuthCard';
import { AuthHeader } from '@/features/identity/components/AuthHeader';
import { useVerifyEmail } from '@/features/identity/hooks/useVerifyEmail';

/** The Verify Email screen's form, including a "resend code" action (`/auth/resend-verification`)
 * with a client-side cooldown. */
export function VerifyEmailForm() {
  const { form, onSubmit, serverError, verified, hasPrefillEmail, resend, resendCooldown } =
    useVerifyEmail();
  const { control, formState } = form;

  if (verified) {
    return (
      <AuthCard>
        <AuthHeader title="Email verified" subtitle="Redirecting you to sign in…" />
      </AuthCard>
    );
  }

  return (
    <AuthCard>
      <AuthHeader
        title="Verify your email"
        subtitle="Enter the 6-digit code we sent to your inbox."
      />
      <Stack component="form" onSubmit={onSubmit} spacing={2} noValidate>
        {serverError ? <Alert severity="error">{serverError}</Alert> : null}
        {!hasPrefillEmail ? (
          <TextField
            name="email"
            control={control}
            label="Email"
            type="email"
            autoComplete="email"
          />
        ) : null}
        <Stack spacing={1} alignItems="center">
          <Typography variant="caption" color="text.secondary">
            Verification code
          </Typography>
          <OtpField name="otp" control={control} />
        </Stack>
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={formState.isSubmitting}
          startIcon={
            formState.isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          Verify email
        </Button>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Didn&apos;t get a code?{' '}
          {resendCooldown > 0 ? (
            <span>Resend in {resendCooldown}s</span>
          ) : (
            <Link component="button" type="button" onClick={resend}>
              Resend code
            </Link>
          )}
        </Typography>
      </Stack>
    </AuthCard>
  );
}
